<script setup lang="ts">
import type { AvatarUnitState } from '../../components/preprocess/avatarUnitState'
import type { PhotoTransitionOrigin } from '../../components/preprocess/photoTransition'
import type { PersistedCreateFlow } from '../../composables/preprocess/draftStore'
import type { AvatarEmotion, FlowMetadata, PreprocessRequestContext, PreprocessSnapshot } from '../../composables/preprocess/types'

import { storeToRefs } from 'pinia'
import { computed, onActivated, onBeforeUnmount, onDeactivated, onMounted, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

import {
  avatarServiceCancelPreprocessImage,
  avatarServiceConfirmPreprocessImagePlan,
  avatarServiceCreateAvatar3,
  avatarServiceCreatePreprocessImage,
  avatarServiceGetPreprocessImageCandidateDownloadUrl,
  avatarServiceGetPreprocessImageStatus,
  avatarServiceRetryPreprocessImageSystemError,
  avatarServiceSelectPreprocessImageCandidate,
  commonServiceGetR2PresignedPutUrl,
} from '../../client'
import AvatarCreationBalance from '../../components/custom-avatar/AvatarCreationBalance.vue'
import AvatarCreationStarted from '../../components/preprocess/AvatarCreationStarted.vue'
import CreateAvatarSteps from '../../components/preprocess/CreateAvatarSteps.vue'
import NoAvatarCreations from '../../components/preprocess/NoAvatarCreations.vue'
import PreprocessSection from '../../components/preprocess/PreprocessSection.vue'
import { Button } from '../../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { createCreateFlowTracker } from '../../composables/preprocess/createFlowTelemetry'
import { clearCreateDraft, readCreateDraft, updateCreateDraft, writeCreateDraft } from '../../composables/preprocess/draftStore'
import { PREPROCESS_API_KEY } from '../../composables/preprocess/types'
import { useApi } from '../../composables/useApi'
import { useCookieConsent } from '../../composables/useCookieConsent'
import { useCreateAvatarCopy } from '../../composables/useCreateAvatarCopy'
import { useAvatarUnitStore } from '../../stores/avatar-unit'
import { useUserStore } from '../../stores/user'
import { buildCreateAvatarRequestBody, resolveCreateAvatarFaceflowVersion } from '../../utils/avatar-create-request'
import { normalizeAvatarEmotion } from '../../utils/avatar-emotion'
import { clearPendingAvatarUnitCheckout, consumePendingAvatarUnitCheckout, markPendingAvatarUnitCheckoutReturned, readPendingAvatarUnitCheckout } from '../../utils/avatar-unit-checkout'
import { errorToMessage } from '../../utils/error'
import { PostHogTracker } from '../../utils/posthog-tracker'

import '../../components/preprocess/create-avatar.css'

const props = defineProps<{ uploadFirst?: boolean, noCreationsPreview?: boolean }>()
definePage({ meta: { desktopAvatarFallback: '/avatars/custom' } })
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { t: copy } = useCreateAvatarCopy()
const { hasAccepted } = useCookieConsent()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const avatarUnitStore = useAvatarUnitStore()

const isLoadingUser = ref(false)
const userLoadFailed = ref(false)
const isCreating = ref(false)
const createdAvatar = ref<{ applyId: string, name: string, photoUrl: string, transitionOrigin?: PhotoTransitionOrigin } | null>(null)
const createResultUnknown = ref(false)
const preprocessSectionRef = ref<InstanceType<typeof PreprocessSection> | null>(null)
const headerStepsTarget = ref<HTMLElement | null>(null)
const draftUserId = computed(() => user.value?.id ?? null)
// Until the authenticated user's local pointer has been checked, showing the
// empty uploader would falsely imply that no in-progress task exists.
const draftDiscoveryPending = ref(true)
const checkoutStatus = ref<'idle' | 'processing' | 'confirmed' | 'delayed'>('idle')
const CHECKOUT_POLL_DELAYS_MS = [1000, 2000, 3000, 5000, 5000, 5000, 5000, 5000, 5000, 5000]
const FINAL_PRECHECK_TIMEOUT_MS = 30_000
const FINAL_IMAGE_UPLOAD_TIMEOUT_MS = 120_000
const CREATE_AVATAR_TIMEOUT_MS = 30_000
let isUnmounted = false
let focusRefreshListenerAttached = false
let checkoutPolling: Promise<void> | null = null
let hasTrackedInsufficientUnits = false
let finalSubmitVersion = 0
let finalSubmitAbortController: AbortController | null = null
let finalSubmitPhase: 'idle' | 'precheck' | 'upload' | 'create' = 'idle'
const isPageActive = ref(false)

function resetCreationHandoff() {
  if (createdAvatar.value)
    URL.revokeObjectURL(createdAvatar.value.photoUrl)
  createdAvatar.value = null
}

function viewCreatedAvatarProgress() {
  if (!createdAvatar.value || isUnmounted)
    return
  const { applyId } = createdAvatar.value
  void router.push({
    path: '/avatars/custom',
    query: applyId
      ? { refresh: 'true', createdApplyId: applyId }
      : { refresh: 'true' },
  })
}

// A photo is in progress until an explicit discard or confirmed submission.
const flowActive = ref(false)
const flowBusy = ref(false)
const showLeaveDialog = ref(false)
let resolveLeave: ((leave: boolean) => void) | null = null
let reviewingUnknownResult = false

onBeforeRouteLeave(() => {
  if (isCreating.value || flowBusy.value) {
    toast.info(copy('waitForPhoto'))
    return false
  }
  if (!flowActive.value || createResultUnknown.value || reviewingUnknownResult)
    return true
  resolveLeave?.(false)
  showLeaveDialog.value = true
  return new Promise<boolean>((resolve) => {
    resolveLeave = resolve
  })
})

function finishLeave(leave: boolean) {
  const resolve = resolveLeave
  resolveLeave = null
  if (leave)
    preprocessSectionRef.value?.discard()
  showLeaveDialog.value = false
  resolve?.(leave)
}

watch(showLeaveDialog, (open) => {
  if (!open && resolveLeave)
    finishLeave(false)
})

onBeforeUnmount(() => resolveLeave?.(false))

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function withRequestDeadline<T>(
  timeoutMs: number,
  request: (signal: AbortSignal) => Promise<T>,
  parentSignal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  let onParentAbort = () => {}
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const error = new DOMException('Request timed out', 'TimeoutError')
      controller.abort(error)
      reject(error)
    }, timeoutMs)
  })
  const parentAbort = new Promise<never>((_, reject) => {
    onParentAbort = () => {
      const error = parentSignal?.reason ?? new DOMException('Request aborted', 'AbortError')
      controller.abort(error)
      reject(error)
    }
    if (parentSignal?.aborted)
      onParentAbort()
    else
      parentSignal?.addEventListener('abort', onParentAbort, { once: true })
  })

  try {
    // Abort cooperative fetches and reject the caller independently so even a
    // non-cooperative operation cannot keep the submit UI pending forever.
    const operation = Promise.resolve().then(() => request(controller.signal))
    return await Promise.race([operation, timeout, parentAbort])
  }
  finally {
    if (timer !== undefined)
      clearTimeout(timer)
    parentSignal?.removeEventListener('abort', onParentAbort)
  }
}

function abortFinalSubmit(): boolean {
  const createMayHaveCommitted = Boolean(finalSubmitAbortController) && finalSubmitPhase === 'create'
  finalSubmitVersion += 1
  finalSubmitAbortController?.abort(new DOMException('Page left', 'AbortError'))
  finalSubmitAbortController = null
  finalSubmitPhase = 'idle'
  isCreating.value = false
  preprocessSectionRef.value?.setSubmitting(false)
  if (createMayHaveCommitted) {
    createResultUnknown.value = true
    if (draftUserId.value)
      updateCreateDraft(draftUserId.value, { status: 'result_unknown' })
  }
  return createMayHaveCommitted
}

function attachFocusRefreshListener() {
  if (typeof window === 'undefined' || focusRefreshListenerAttached)
    return
  window.addEventListener('focus', refreshAvatarUnitsOnFocus)
  focusRefreshListenerAttached = true
}

function detachFocusRefreshListener() {
  if (typeof window === 'undefined' || !focusRefreshListenerAttached)
    return
  window.removeEventListener('focus', refreshAvatarUnitsOnFocus)
  focusRefreshListenerAttached = false
}

async function refreshAvatarUnitsOnFocus() {
  const costUnits = avatarUnitStore.defaultPipelineCost || 1
  if (checkoutStatus.value === 'idle' && avatarUnitStore.hasFetchedSummary && avatarUnitStore.availableUnits >= costUnits)
    return
  try {
    await avatarUnitStore.fetchSummary({ force: true, silent: true })
  }
  catch (error) {
    console.warn('Failed to refresh avatar creations on focus:', error)
  }
}

async function clearCheckoutCallbackQuery() {
  if (route.query.checkout === undefined)
    return
  const { checkout: _checkout, package: _package, source: _source, return_path: _returnPath, ...query } = route.query
  await router.replace({ path: route.path, query })
}

function confirmPendingCheckout(expectedStartedAt: number): boolean {
  const pending = consumePendingAvatarUnitCheckout(expectedStartedAt)
  if (!pending)
    return false
  checkoutStatus.value = 'confirmed'
  PostHogTracker.payment.checkoutSucceeded({
    productType: 'avatar_units',
    packageId: pending.packageId,
    source: pending.source,
  })
  return true
}

async function runCheckoutPolling() {
  const pending = readPendingAvatarUnitCheckout()
  checkoutStatus.value = 'processing'

  if (!pending) {
    checkoutStatus.value = 'delayed'
    await clearCheckoutCallbackQuery()
    return
  }
  if (!pending.successReturnedAt) {
    checkoutStatus.value = 'idle'
    return
  }

  for (const delay of CHECKOUT_POLL_DELAYS_MS) {
    if (isUnmounted)
      return
    await avatarUnitStore.fetchSummary({ force: true, silent: true })
    if (isUnmounted)
      return
    if (avatarUnitStore.availableUnits >= pending.expectedAvailable) {
      if (!confirmPendingCheckout(pending.startedAt))
        checkoutStatus.value = 'delayed'
      await clearCheckoutCallbackQuery()
      return
    }
    await wait(delay)
  }

  if (isUnmounted)
    return
  await avatarUnitStore.fetchSummary({ force: true, silent: true })
  if (isUnmounted)
    return
  if (avatarUnitStore.availableUnits >= pending.expectedAvailable) {
    if (!confirmPendingCheckout(pending.startedAt))
      checkoutStatus.value = 'delayed'
    await clearCheckoutCallbackQuery()
    return
  }
  checkoutStatus.value = 'delayed'
  await clearCheckoutCallbackQuery()
}

function startCheckoutPolling() {
  if (checkoutPolling)
    return checkoutPolling
  checkoutPolling = runCheckoutPolling().finally(() => {
    checkoutPolling = null
  })
  return checkoutPolling
}

function resumePendingCheckout() {
  const pending = readPendingAvatarUnitCheckout()
  if (route.query.checkout === 'cancel' || !pending?.successReturnedAt || checkoutStatus.value === 'confirmed')
    return
  if (checkoutPolling) {
    const currentPolling = checkoutPolling
    void currentPolling.then(() => {
      if (!isUnmounted && checkoutStatus.value === 'processing' && readPendingAvatarUnitCheckout())
        startCheckoutPolling()
    })
    return
  }
  startCheckoutPolling()
}

async function handleCheckoutReturn(value: unknown) {
  if (value === 'success') {
    markPendingAvatarUnitCheckoutReturned()
    startCheckoutPolling()
  }
  else if (value === 'cancel') {
    clearPendingAvatarUnitCheckout()
    checkoutStatus.value = 'idle'
    toast.info(t('avatarUnits.checkout.canceled'))
    await clearCheckoutCallbackQuery()
  }
}

onMounted(async () => {
  attachFocusRefreshListener()

  // Resolve identity first. Draft discovery reacts to `user.id` below, so it
  // can re-attach to the backend task without waiting for the unrelated
  // balance / pipeline requests used by the upload gate.
  isLoadingUser.value = true
  userLoadFailed.value = false
  try {
    await userStore.me()
  }
  catch (error) {
    userLoadFailed.value = true
    console.warn('Failed to fetch user data:', error)
  }
  finally {
    isLoadingUser.value = false
    if (!user.value)
      draftDiscoveryPending.value = false
  }

  try {
    await Promise.all([
      avatarUnitStore.fetchSummary({ force: true, silent: true }),
      avatarUnitStore.fetchPipelines({ silent: true }),
    ])
  }
  catch (error) {
    console.warn('Failed to fetch Avatar Creation data:', error)
  }

  await handleCheckoutReturn(route.query.checkout)
  if (route.query.checkout !== 'success')
    resumePendingCheckout()
})

watch(() => route.query.checkout, (value) => {
  handleCheckoutReturn(value)
})

onBeforeUnmount(() => {
  isUnmounted = true
  resetCreationHandoff()
  abortFinalSubmit()
  isPageActive.value = false
  detachFocusRefreshListener()
})

onDeactivated(() => {
  isUnmounted = true
  resetCreationHandoff()
  abortFinalSubmit()
  isPageActive.value = false
  detachFocusRefreshListener()
  hasTrackedInsufficientUnits = false
})

onActivated(() => {
  reviewingUnknownResult = false
  isUnmounted = false
  isPageActive.value = true
  attachFocusRefreshListener()
  resumePendingCheckout()
  trackInsufficientUnits()
})

const preprocessKey = ref(0)
const avatarUnitState = computed<AvatarUnitState>(() => {
  const costUnits = avatarUnitStore.defaultPipelineCost || 1
  if (userLoadFailed.value)
    return { status: 'error', availableUnits: 0, costUnits }
  if (!user.value || isLoadingUser.value) {
    return { status: 'loading', availableUnits: 0, costUnits }
  }
  if ((avatarUnitStore.isLoadingSummary && !avatarUnitStore.hasFetchedSummary)
    || (avatarUnitStore.isLoadingPipelines && !avatarUnitStore.hasFetchedPipelines)) {
    return { status: 'loading', availableUnits: 0, costUnits }
  }
  const availableUnits = avatarUnitStore.availableUnits
  if (availableUnits >= costUnits) {
    return { status: 'available', availableUnits, costUnits }
  }
  if (checkoutStatus.value === 'processing' || checkoutStatus.value === 'delayed') {
    return { status: 'pending', availableUnits, costUnits }
  }
  if (avatarUnitStore.summaryLoadFailed || !avatarUnitStore.hasFetchedPipelines) {
    return { status: 'error', availableUnits, costUnits }
  }
  return { status: 'empty', availableUnits, costUnits }
})

function trackInsufficientUnits() {
  const state = avatarUnitState.value
  if (!isPageActive.value || hasTrackedInsufficientUnits || state.status !== 'empty')
    return
  if (PostHogTracker.avatar.creationBlocked(state.availableUnits, state.costUnits))
    hasTrackedInsufficientUnits = true
}

watch([() => avatarUnitState.value.status, hasAccepted], trackInsufficientUnits, { immediate: true })

provide(PREPROCESS_API_KEY, {
  uploadSourceImage,
  createTask: createPreprocessTask,
  fetchTask: fetchPreprocessTask,
  getCandidateDownloadURL: getPreprocessCandidateDownloadURL,
  confirmPlan: confirmPreprocessPlan,
  selectCandidate: selectPreprocessCandidate,
  retrySystemError: retryPreprocessSystemError,
  cancelTask: cancelPreprocessTask,
})

// --- Draft pointer persistence (requirement 7) ---------------------------
// Saves the task pointer and form fields per user, plus optional consented
// analytics. Recovery re-reads the backend task; no image bytes or tokens.

// Latest form name as reported by PreprocessSection, mirrored into the
// draft so recovery can refill the name field (consent is never restored).
const draftName = ref('')
const draftEmotion = ref<AvatarEmotion>('neutral')

// Funnel analytics: one create flow per avatar creation. Each selected photo
// opens or advances it, avatar_created or a discard ends it, and it rides on
// the draft pointer so a refreshed page reports into the same flow.
const createFlow = createCreateFlowTracker({ enabled: () => hasAccepted.value, onChange: persistCreateFlow })

function persistCreateFlow() {
  const userId = draftUserId.value
  if (userId)
    updateCreateDraft(userId, { createFlow: createFlow.persisted() })
}

const resumeInput = ref<{ taskId: string, attemptsUsed: number, name: string, emotion?: AvatarEmotion, createFlow?: PersistedCreateFlow } | null>(null)
watch(hasAccepted, (accepted) => {
  if (!accepted) {
    createFlow.end()
    if (resumeInput.value)
      resumeInput.value.createFlow = undefined
  }
  else if (flowActive.value && !createFlow.context()) {
    // Consent granted mid-creation: the missing beginning must not count as a full funnel.
    createFlow.restore(undefined)
  }
})
const showNoCreationsEntry = computed(() => avatarUnitState.value.status === 'empty'
  && !createdAvatar.value
  && !createResultUnknown.value
  && !flowActive.value
  && !resumeInput.value
  && !draftDiscoveryPending.value)
const showEntryCreationBalance = computed(() => avatarUnitState.value.status === 'available'
  && !flowBusy.value
  && !createdAvatar.value
  && !createResultUnknown.value
  && !flowActive.value
  && !resumeInput.value
  && !draftDiscoveryPending.value)
let discoveredDraftUserId: string | null = null

function handleTaskCheckpoint(info: { taskId: string, attemptsUsed: number }) {
  const userId = draftUserId.value
  if (!userId)
    return
  writeCreateDraft(userId, {
    taskId: info.taskId,
    attemptsUsed: info.attemptsUsed,
    status: 'processing',
    name: draftName.value,
    emotion: draftEmotion.value,
    createFlow: createFlow.persisted(),
  })
}

function handleRestoreFailed(reason: 'gone' | 'unavailable') {
  const userId = draftUserId.value
  const draft = userId ? readCreateDraft(userId) : null
  const mustPreserveUnknownResult = createResultUnknown.value
    || draft?.status === 'submitting'
    || draft?.status === 'result_unknown'
  // Recovery no longer owns an active on-screen flow once the provider has
  // returned to idle. Restore the full guide/upload layout as well.
  flowActive.value = false
  if (reason === 'gone') {
    // A missing preprocessing task cannot prove that the later, non-idempotent
    // Create Avatar request did not commit. Preserve that separate guard until
    // the user has checked Custom Avatars.
    if (mustPreserveUnknownResult) {
      createResultUnknown.value = true
      toast.info(t('avatarUnits.createGate.unknownResultDescription'))
      return
    }
    if (userId)
      clearCreateDraft(userId)
    // The task behind this flow no longer exists; a new photo starts afresh.
    createFlow.end()
    createResultUnknown.value = false
    toast.info(t('avatars.create.preprocess.restore.goneToast'))
  }
  else {
    // A temporary read failure says nothing about whether a submitting or
    // result-unknown request reached the backend. Keep that guard intact.
    toast.info(t('avatars.create.preprocess.restore.unavailableToast'))
  }
}

function handleDraftNameChange(name: string) {
  draftName.value = name
  const userId = draftUserId.value
  if (userId)
    updateCreateDraft(userId, { name })
}

function handleDraftEmotionChange(emotion: AvatarEmotion) {
  draftEmotion.value = emotion
  const userId = draftUserId.value
  if (userId)
    updateCreateDraft(userId, { emotion })
}

function handleFormReached() {
  const userId = draftUserId.value
  if (!userId)
    return
  // Only upgrade from 'processing'. A restored submitting/result_unknown
  // pointer also lands on the form — that state must survive the landing,
  // or a second refresh would silently re-open submission.
  const draft = readCreateDraft(userId)
  if (draft?.status === 'processing')
    updateCreateDraft(userId, { status: 'form' })
}

function handleResumed() {
  // The section re-attaches to the persisted task now: continue its flow.
  createFlow.restore(resumeInput.value?.createFlow)
  resumeInput.value = null
  flowActive.value = true
}

function handleCreationStarted() {
  // Emitted synchronously before the section starts the photo, so every
  // event of this photo carries the flow fields opened here.
  PostHogTracker.avatar.creationStarted(createFlow.beginPhoto())
  // Selecting a new photo after an unavailable restore is an explicit
  // abandonment of the old processing draft. Clear it now instead of waiting
  // for a new backend checkpoint: local validation, upload, or task creation
  // can fail before that checkpoint and must not resurrect the old task on
  // the next refresh. The result-unknown guard never reaches this handler —
  // PreprocessSection blocks its uploader and points the user to Custom Avatars.
  const userId = draftUserId.value
  if (userId)
    clearCreateDraft(userId)
  resumeInput.value = null
  draftName.value = ''
  draftEmotion.value = 'neutral'
  createResultUnknown.value = false
  flowActive.value = true
}

// Reads the persisted pointer as soon as the authenticated identity becomes
// available. The per-user guard makes this one-shot for a cached page, while
// the `flowActive` guard ensures a live KeepAlive flow always wins.
function discoverCreateDraft(userId: string) {
  const draft = readCreateDraft(userId)
  if (!draft)
    return
  if (draft.status === 'submitting' || draft.status === 'result_unknown') {
    // The prior session died inside/after the create call. Do not allow a
    // resubmit until the user has checked Custom Avatars.
    createResultUnknown.value = true
  }
  draftName.value = draft.name
  draftEmotion.value = normalizeAvatarEmotion(draft.emotion)
  resumeInput.value = {
    taskId: draft.taskId,
    attemptsUsed: draft.attemptsUsed,
    name: draft.name,
    emotion: normalizeAvatarEmotion(draft.emotion),
    createFlow: draft.createFlow,
  }
}

watch(draftUserId, (userId) => {
  if (!userId) {
    draftDiscoveryPending.value = true
    return
  }

  if (discoveredDraftUserId === userId) {
    draftDiscoveryPending.value = false
    return
  }

  discoveredDraftUserId = userId
  draftDiscoveryPending.value = true
  if (!flowActive.value && !resumeInput.value)
    discoverCreateDraft(userId)
  draftDiscoveryPending.value = false
}, { immediate: true, flush: 'sync' })

async function refreshAvatarUnits() {
  checkoutStatus.value = 'idle'
  userLoadFailed.value = false
  if (!user.value) {
    isLoadingUser.value = true
    try {
      await userStore.me()
    }
    catch {
      userLoadFailed.value = true
      return
    }
    finally {
      isLoadingUser.value = false
    }
  }
  await Promise.all([
    avatarUnitStore.fetchSummary({ force: true, silent: false }),
    avatarUnitStore.fetchPipelines({ silent: false }),
  ])
}

function openPersonalAvatars() {
  reviewingUnknownResult = true
  router.push('/avatars/custom?refresh=true')
}

function currentApiError(error: unknown): unknown {
  const cause = error instanceof Error && error.cause ? error.cause : error
  if (cause && typeof cause === 'object' && 'data' in cause)
    return (cause as { data?: unknown }).data ?? cause
  return cause
}

function currentErrorMessage(error: unknown) {
  return errorToMessage(currentApiError(error)) || (error instanceof Error ? error.message : String(error || ''))
}

function isInsufficientUnitsError(error: unknown) {
  const message = currentErrorMessage(error).toLowerCase()
  // Keep old backend wording during independent frontend/backend rollout.
  return message.includes('insufficient avatar creations') || message.includes('insufficient avatar slots') || message.includes('insufficient avatar units')
}

function isConcurrentLimitError(error: unknown) {
  return currentErrorMessage(error).toLowerCase().includes('concurrent limit')
}

function currentApiStatus(error: unknown) {
  const apiError = currentApiError(error)
  if (apiError && typeof apiError === 'object') {
    const maybeApiError = apiError as { error?: { status?: number | string }, status?: number | string }
    const status = Number(maybeApiError.error?.status ?? maybeApiError.status)
    return Number.isFinite(status) ? status : undefined
  }
  return undefined
}

function isAmbiguousCreateError(error: unknown) {
  const status = currentApiStatus(error)
  return typeof status !== 'number' || status >= 500 || [408, 425, 429].includes(status)
}

type AvatarUnitPrecheckResult = 'available' | 'insufficient' | 'unavailable'

async function checkAvatarUnitsForCreate(signal?: AbortSignal): Promise<AvatarUnitPrecheckResult> {
  await Promise.all([
    avatarUnitStore.fetchSummary({ force: true, silent: true, signal }),
    avatarUnitStore.fetchPipelines({ silent: true, signal }),
  ])
  if (signal?.aborted)
    throw signal.reason ?? new DOMException('Request aborted', 'AbortError')
  const costUnits = avatarUnitStore.defaultPipelineCost || 1
  if (!avatarUnitStore.hasFetchedPipelines)
    return 'unavailable'
  if (avatarUnitStore.hasFetchedSummary && avatarUnitStore.availableUnits >= costUnits)
    return 'available'
  if (avatarUnitStore.summaryLoadFailed || !avatarUnitStore.hasFetchedSummary)
    return 'unavailable'
  return 'insufficient'
}

function showAvatarUnitPrecheckFailure(result: Exclude<AvatarUnitPrecheckResult, 'available'>) {
  if (result === 'insufficient') {
    toast.info(t('avatarUnits.createGate.emptyDescription', { cost: avatarUnitStore.defaultPipelineCost || 1 }))
    return
  }
  toast.error(t('avatarUnits.createGate.errorDescription'))
}

async function handlePreprocessSubmit(file: File, name: string, metadata: FlowMetadata) {
  // The review form includes the cost and consent. Its primary action commits
  // directly, with the same authoritative quota check as the final request.
  await submitCreateAvatar({ file, name, metadata })
}

function handlePreprocessReset() {
  const createMayHaveCommitted = abortFinalSubmit()
  // Once the create result is ambiguous, keep the durable guard even after
  // the request has already settled. Clearing it would allow a second create
  // request for an Avatar that may already exist (and may already be billed).
  if (createResultUnknown.value || createMayHaveCommitted)
    return
  flowActive.value = false
  createResultUnknown.value = false
  // Discard / re-upload abandons the current photo entirely — the pointer
  // must die with it (this is also what resets the re-run budget).
  const userId = draftUserId.value
  if (userId)
    clearCreateDraft(userId)
  draftName.value = ''
  draftEmotion.value = 'neutral'
}

interface R2UploadResult {
  objectKey: string
  publicUrl: string
}

const R2_PUBLIC_BASE_URL = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || 'https://assets.spatius.ai').replace(/\/$/, '')

function publicUrlFromR2ObjectKey(objectKey: string): string {
  return new URL(objectKey.replace(/^\/+/, ''), `${R2_PUBLIC_BASE_URL}/`).toString()
}

async function uploadFileToR2(file: File, signal?: AbortSignal): Promise<R2UploadResult> {
  const { data } = (await useApi(commonServiceGetR2PresignedPutUrl, {
    body: {
      businessType: 'R2_BUSINESS_TYPE_AVATAR' as const,
      contentType: file.type || 'image/jpeg',
      durationSeconds: '3600',
    },
    signal,
  }, { silent: true })).expect('Failed to get upload URL')

  if (!data || !('presignedUrl' in data) || !data.presignedUrl || !('objectKey' in data) || !data.objectKey)
    throw new Error('Failed to get presigned URL')

  const fileBuffer = await file.arrayBuffer()
  const uploadResponse = await fetch(data.presignedUrl, {
    method: 'PUT',
    headers: { 'x-amz-acl': 'public-read' },
    body: fileBuffer,
    signal,
  })

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text().catch(() => 'Unknown error')
    throw new Error(`Failed to upload file: ${uploadResponse.status} ${errorText}`)
  }

  return {
    objectKey: data.objectKey,
    publicUrl: (data as { publicUrl?: string }).publicUrl ?? publicUrlFromR2ObjectKey(data.objectKey),
  }
}

async function uploadSourceImage(file: File, { signal }: PreprocessRequestContext) {
  const uploaded = await uploadFileToR2(file, signal)
  return {
    imageUrl: uploaded.publicUrl,
    objectKey: uploaded.objectKey,
  }
}

async function createPreprocessTask(imageUrl: string, { signal }: PreprocessRequestContext) {
  const { data } = (await useApi(avatarServiceCreatePreprocessImage, {
    body: { imageUrl },
    signal,
  }, { silent: true })).expect('Failed to create preprocess task')

  if (!data?.taskId)
    throw new Error('Failed to create preprocess task')

  return {
    taskId: data.taskId,
  }
}

// Field-debug hooks for the real pipeline: the last snapshot per task is
// kept on window (dev / preview / test builds only) and postprocess state
// changes are logged under one tag, so "the background never showed up"
// can be settled by reading what the backend actually reported.
const PREPROCESS_DEBUG = (import.meta.env.VITE_ENV || (import.meta.env.DEV ? 'development' : 'production')) !== 'production'
let lastLoggedPostprocess = ''
function recordPreprocessSnapshot(snapshot: PreprocessSnapshot) {
  if (!PREPROCESS_DEBUG)
    return
  const debugWindow = window as unknown as { __preprocessLast?: PreprocessSnapshot }
  debugWindow.__preprocessLast = snapshot
  const bg = snapshot.postprocess?.background
  const key = `${snapshot.taskId}|${snapshot.lifecycleStatus}|${snapshot.preprocessingState}|${bg?.status}|${bg?.displayStatus}|${bg?.imageUrl ? 'url' : 'nourl'}`
  if (key !== lastLoggedPostprocess) {
    lastLoggedPostprocess = key
    console.warn('[preprocess] task', snapshot.taskId, 'lifecycle', snapshot.lifecycleStatus, 'state', snapshot.preprocessingState, 'background', bg ?? '(absent)')
  }
}
function handleProviderDiagnostic(diagnostic: unknown) {
  if (PREPROCESS_DEBUG)
    console.warn('[preprocess] diagnostic', diagnostic)
}

async function fetchPreprocessTask(taskId: string, { signal }: PreprocessRequestContext): Promise<PreprocessSnapshot> {
  // silent: the preprocess provider owns error UX (system-error card /
  // restore fallback); a toast per failed poll would just stack noise.
  const { data } = (await useApi(avatarServiceGetPreprocessImageStatus, {
    path: { taskId },
    signal,
  }, { silent: true })).expect('Failed to fetch preprocess task')

  if (!data)
    throw new Error('Failed to fetch preprocess task')
  recordPreprocessSnapshot(data)
  return data
}

async function getPreprocessCandidateDownloadURL(taskId: string, candidateId: string, { signal }: PreprocessRequestContext): Promise<string> {
  const { data } = (await useApi(avatarServiceGetPreprocessImageCandidateDownloadUrl, {
    path: { taskId, candidateId },
    signal,
  }, { silent: true })).expect('Failed to get preprocess candidate download URL')

  if (!data?.downloadUrl)
    throw new Error('Preprocess candidate download URL is empty')
  return data.downloadUrl
}

async function confirmPreprocessPlan(taskId: string, expectedRevision: string, idempotencyKey: string, { signal }: PreprocessRequestContext): Promise<PreprocessSnapshot> {
  const { data } = (await useApi(avatarServiceConfirmPreprocessImagePlan, {
    path: { taskId },
    body: { expectedRevision, idempotencyKey },
    signal,
  }, { silent: true })).expect('Failed to confirm preprocess plan')
  if (!data?.task)
    throw new Error('Preprocess command returned no task')
  return data.task
}

async function selectPreprocessCandidate(taskId: string, candidateId: string, expectedRevision: string, idempotencyKey: string, { signal }: PreprocessRequestContext): Promise<PreprocessSnapshot> {
  const { data } = (await useApi(avatarServiceSelectPreprocessImageCandidate, {
    path: { taskId },
    body: { candidateId, expectedRevision, idempotencyKey },
    signal,
  }, { silent: true })).expect('Failed to select preprocess candidate')
  if (!data?.task)
    throw new Error('Preprocess command returned no task')
  return data.task
}

async function retryPreprocessSystemError(taskId: string, expectedRevision: string, idempotencyKey: string, { signal }: PreprocessRequestContext): Promise<PreprocessSnapshot> {
  const { data } = (await useApi(avatarServiceRetryPreprocessImageSystemError, {
    path: { taskId },
    body: { expectedRevision, idempotencyKey },
    signal,
  }, { silent: true })).expect('Failed to retry preprocess task')
  if (!data?.task)
    throw new Error('Preprocess command returned no replacement task')
  return data.task
}

async function cancelPreprocessTask(taskId: string, expectedRevision: string, idempotencyKey: string, { signal }: PreprocessRequestContext): Promise<void> {
  ;(await useApi(avatarServiceCancelPreprocessImage, {
    path: { taskId },
    body: { expectedRevision, idempotencyKey },
    signal,
  }, { silent: true })).expect('Failed to cancel preprocess task')
}

async function submitCreateAvatar(submit: { file: File, name: string, metadata: FlowMetadata }) {
  if (isCreating.value || createdAvatar.value || createResultUnknown.value || isUnmounted)
    return

  const submitVersion = ++finalSubmitVersion
  const abortController = new AbortController()
  finalSubmitAbortController = abortController
  finalSubmitPhase = 'precheck'
  isCreating.value = true
  preprocessSectionRef.value?.setSubmitting(true)
  let created = false
  let submitPhase: 'precheck' | 'upload' | 'create' = 'precheck'
  try {
    const precheck = await withRequestDeadline(
      FINAL_PRECHECK_TIMEOUT_MS,
      signal => checkAvatarUnitsForCreate(signal),
      abortController.signal,
    )
    if (submitVersion !== finalSubmitVersion || isUnmounted)
      return
    if (precheck !== 'available') {
      PostHogTracker.avatar.creationFailed(
        precheck === 'insufficient' ? 'insufficient_avatar_creations' : 'avatar_creations_unavailable',
        '',
        { flow: createFlow.context(), phase: 'precheck' },
      )
      showAvatarUnitPrecheckFailure(precheck)
      return
    }

    PostHogTracker.avatar.generationStarted({
      ...submit.metadata,
      avatarEmotion: submit.metadata.avatarEmotion,
      pipelineVersion: resolveCreateAvatarFaceflowVersion(submit.metadata),
      selectionSource: submit.metadata.avatarEmotion === undefined ? 'legacy' : 'user',
    }, createFlow.context())
    submitPhase = 'upload'
    finalSubmitPhase = 'upload'
    toast.info(t('avatars.create.uploadingImage'))
    const { objectKey: imagePath } = await withRequestDeadline(
      FINAL_IMAGE_UPLOAD_TIMEOUT_MS,
      signal => uploadFileToR2(submit.file, signal),
      abortController.signal,
    )
    if (submitVersion !== finalSubmitVersion || isUnmounted)
      return

    submitPhase = 'create'
    finalSubmitPhase = 'create'
    // Only the create call itself can double-charge. precheck/upload
    // failures or refreshes are safe to redo, so they never mark this.
    if (draftUserId.value)
      updateCreateDraft(draftUserId.value, { status: 'submitting' })
    toast.info(t('avatars.create.creatingAvatar'))
    const requestBody = buildCreateAvatarRequestBody({
      imagePath,
      avatarName: submit.name,
      metadata: submit.metadata,
    })

    const { data } = await withRequestDeadline(CREATE_AVATAR_TIMEOUT_MS, async signal => (
      await useApi(avatarServiceCreateAvatar3, {
        body: requestBody,
        signal,
      }, { silent: true })
    ).expect('Failed to create avatar'), abortController.signal)

    if (submitVersion !== finalSubmitVersion || isUnmounted)
      return
    // The response is authoritative now. A route transition caused by the
    // success path must not be mistaken for an in-flight ambiguous create.
    finalSubmitAbortController = null
    finalSubmitPhase = 'idle'

    const applyId = data?.id || ''
    PostHogTracker.avatar.created(applyId, submit.name, false, createFlow.context())
    createFlow.end()

    toast.success(t('avatars.create.createdSuccessfully'))
    userStore.me().catch(err => console.warn('Failed to refresh user data:', err))
    avatarUnitStore.fetchSummary({ force: true, silent: true }).catch(err => console.warn('Failed to refresh avatar creations:', err))
    createResultUnknown.value = false
    if (draftUserId.value)
      clearCreateDraft(draftUserId.value)
    resumeInput.value = null
    draftName.value = ''
    draftEmotion.value = 'neutral'
    // Measure only after success, so scrolling while the request is pending
    // cannot leave the portrait animation with stale viewport coordinates.
    const transitionOrigin = preprocessSectionRef.value?.captureReviewPhotoOrigin()
    createdAvatar.value = { applyId, name: submit.name, photoUrl: URL.createObjectURL(submit.file), transitionOrigin }
    preprocessKey.value += 1
    flowActive.value = false
    created = true
  }
  catch (error) {
    if (submitVersion !== finalSubmitVersion || isUnmounted)
      return
    // The request has settled (even if its outcome is ambiguous). Do not let
    // a later route transition treat follow-up error handling as an in-flight
    // Create request.
    finalSubmitAbortController = null
    finalSubmitPhase = 'idle'
    const errorMessage = currentErrorMessage(error)
    if (isInsufficientUnitsError(error)) {
      createResultUnknown.value = false
      void avatarUnitStore.fetchSummary({ force: true, silent: true })
      toast.info(t('avatarUnits.createGate.emptyDescription', { cost: avatarUnitStore.defaultPipelineCost || 1 }))
    }
    else if (isConcurrentLimitError(error)) {
      createResultUnknown.value = false
      toast.error(t('avatarUnits.createGate.concurrentLimit'))
    }
    else if (submitPhase === 'create' && isAmbiguousCreateError(error)) {
      createResultUnknown.value = true
      toast.info(t('avatarUnits.createGate.unknownResultTitle'))
    }
    else {
      createResultUnknown.value = false
      toast.error(errorMessage || t('avatars.create.createFailed'))
    }
    PostHogTracker.avatar.creationFailed('create_failed', errorMessage, {
      flow: createFlow.context(),
      phase: submitPhase,
      resultUnknown: createResultUnknown.value,
    })
    // Definitive failures reopen the form; only an ambiguous create error
    // keeps the pointer in result_unknown so a refresh stays blocked too.
    if (draftUserId.value)
      updateCreateDraft(draftUserId.value, { status: createResultUnknown.value ? 'result_unknown' : 'form' })
  }
  finally {
    if (submitVersion === finalSubmitVersion) {
      finalSubmitAbortController = null
      finalSubmitPhase = 'idle'
      isCreating.value = false
      if (!created)
        preprocessSectionRef.value?.setSubmitting(false)
    }
  }
}
</script>

<template>
  <div class="studio-ui create-avatar-page" data-testid="create-avatar-page">
    <header class="create-header">
      <div class="create-header-title">
        <h1>
          {{ t('avatars.create.title') }}
        </h1>
        <AvatarCreationBalance
          v-if="showEntryCreationBalance"
          class="create-header-balance"
          variant="caption"
          data-testid="create-avatar-balance"
          :available-units="avatarUnitState.availableUnits"
        />
      </div>
      <div ref="headerStepsTarget" class="create-header-steps">
        <CreateAvatarSteps
          v-if="createdAvatar"
          :steps="[copy('upload'), copy('checkPrepare'), copy('review')]"
          :current="4"
          :label="t('avatars.create.title')"
          compact
        />
      </div>
    </header>
    <AvatarCreationStarted
      v-if="createdAvatar"
      :name="createdAvatar.name"
      :photo-url="createdAvatar.photoUrl"
      :transition-origin="createdAvatar.transitionOrigin"
      @view-avatars="viewCreatedAvatarProgress"
    />
    <div v-else-if="draftDiscoveryPending && !flowActive" class="min-h-0 flex flex-1 items-center justify-center" data-testid="create-draft-discovery" role="status" :aria-label="t('common.loading')">
      <i class="i-lucide-loader-2 h-7 w-7 animate-spin text-vis-mid" aria-hidden="true" />
      <span class="sr-only">{{ t('common.loading') }}</span>
    </div>
    <NoAvatarCreations v-else-if="showNoCreationsEntry" :cost-units="avatarUnitState.costUnits" />
    <PreprocessSection
      v-else
      ref="preprocessSectionRef"
      :key="preprocessKey"
      :steps-target="headerStepsTarget"
      :upload-first="props.uploadFirst"
      :avatar-unit-state="avatarUnitState"
      :create-flow="createFlow"
      :create-result-unknown="createResultUnknown"
      :resume-input="resumeInput"
      @creation-started="handleCreationStarted"
      @busy-change="flowBusy = $event"
      @retry-units="refreshAvatarUnits"
      @view-personal="openPersonalAvatars"
      @submit="handlePreprocessSubmit"
      @reset="handlePreprocessReset"
      @resumed="handleResumed"
      @draft-name-change="handleDraftNameChange"
      @draft-emotion-change="handleDraftEmotionChange"
      @form-reached="handleFormReached"
      @task-checkpoint="handleTaskCheckpoint"
      @restore-failed="handleRestoreFailed"
      @diagnostic="handleProviderDiagnostic"
    />
    <Dialog v-model:open="showLeaveDialog">
      <DialogContent class="studio-ui create-avatar-dialog sm:max-w-[440px]" overlay-class="create-avatar-dialog-overlay">
        <DialogTitle>{{ copy('leaveTitle') }}</DialogTitle>
        <DialogDescription>{{ copy('leaveDescription') }}</DialogDescription>
        <p class="text-sm text-vis-mid">
          {{ copy('noCharge') }}
        </p>
        <div class="flex flex-wrap justify-end gap-3 pt-3">
          <Button @click="finishLeave(false)">
            {{ copy('keepCreating') }}
          </Button>
          <Button variant="outline" @click="finishLeave(true)">
            {{ copy('leaveCreation') }}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>
